import { useState } from 'react'
import { Suggestion } from '../types'
import { apiClient } from '../services/api'
import { useDiscoverRefresh } from '../contexts/DiscoverRefreshContext'
import { relativeTime } from '../utils/articleUtils'
import Header from '../components/Header'
import TabBar from '../components/TabBar'
import SaveArticleForm from '../components/SaveArticleForm'
import { RowThumbnail } from '../components/RowThumbnail'

type SaveState = 'idle' | 'saving' | 'saved'

export default function DiscoverPage() {
  // Feed state lives in the app-level provider so a single poll loop drives both
  // this page (content auto-updates the moment a background refresh lands) and the
  // global "updating / updated" indicator that shows on every view.
  const { suggestions, loading, error, refresh, removeSuggestion } = useDiscoverRefresh()
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({})

  async function handleSave(s: Suggestion) {
    setSaveStates((prev) => ({ ...prev, [s.url]: 'saving' }))
    try {
      await apiClient.createArticle({ url: s.url })
      setSaveStates((prev) => ({ ...prev, [s.url]: 'saved' }))
      apiClient.markSuggestionSaved(s.url).catch(() => {})
    } catch {
      setSaveStates((prev) => {
        const next = { ...prev }
        delete next[s.url]
        return next
      })
    }
  }

  function handleDismiss(s: Suggestion) {
    removeSuggestion(s.url)
    apiClient.dismissSuggestion(s.url).catch(() => {})
  }

  return (
    <>
      <Header />
      <main className="page">
        <SaveArticleForm onSaved={(_article) => {}} />
        <TabBar />

        {loading ? (
          <div className="list-loading">
            <div className="spinner" />
            <p className="list-loading-text">Finding new reads for you…</p>
          </div>
        ) : error ? (
          <div className="empty-state">
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="btn-secondary discover-retry-btn" onClick={() => refresh()}>
              Retry
            </button>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="empty-state">
            <h3>No suggestions yet</h3>
            <p>
              Save a few articles and we&apos;ll put together a personalized feed — check back in a
              minute.
            </p>
          </div>
        ) : (
          <div className="article-list">
            {suggestions.map((s) => {
              const state = saveStates[s.url] ?? 'idle'
              return (
                <div key={s.url} className="article-card">
                  <div className="article-card-inner discover-card-inner">
                    <RowThumbnail imageUrl={s.imageUrl} className="discover-thumb" />
                    <div className="article-card-body">
                      <div className="article-card-meta">
                        <span>{s.siteName}</span>
                        {s.pubDate && (
                          <>
                            <span className="meta-dot">·</span>
                            <span>{relativeTime(s.pubDate)}</span>
                          </>
                        )}
                      </div>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="article-card-title discover-title-link"
                      >
                        {s.title || s.url}
                      </a>
                      {s.excerpt && <p className="article-card-excerpt">{s.excerpt}</p>}
                      <div className="article-card-meta discover-reason">
                        <span>{s.reason}</span>
                      </div>
                      <div className="discover-save-area">
                        {state === 'saved' ? (
                          <span className="saved-label">Saved</span>
                        ) : (
                          <button
                            className="btn-secondary"
                            disabled={state === 'saving'}
                            onClick={() => handleSave(s)}
                          >
                            {state === 'saving' ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="article-card-actions">
                      <button
                        className="btn-ghost"
                        onClick={() => handleDismiss(s)}
                        title="Dismiss"
                        aria-label="Dismiss suggestion"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </>
  )
}
